<script setup lang="ts">
  import { computed } from 'vue';
  import { usePrintHealthStore } from '@/stores/print-health.store';
  import { usePrintHealth } from '@/composition/printing/use-print-health';
  import { usePrintingSettingsModal } from '@/composition/printing/use-printing-settings-modal';
  import { formatJobTime } from '@/composition/printing/use-printing-v2-ui';
  import { IPrintFailureEntry } from '@/lib/print/print-health.utils';

  const emit = defineEmits<{
    (e: 'close'): void;
  }>();

  const healthStore = usePrintHealthStore();
  const { offlineAssignedPrinters } = usePrintHealth();
  const settingsModal = usePrintingSettingsModal();

  const failures = computed(() => healthStore.failures);
  const offline = computed(() => offlineAssignedPrinters.value);
  const allGood = computed(
    () => !failures.value.length && !offline.value.length
  );

  function entryTime(entry: IPrintFailureEntry): string {
    return formatJobTime(entry.lastAt);
  }

  function openSettings() {
    emit('close');
    settingsModal.open();
  }

  function viewJobs() {
    emit('close');
    settingsModal.open({ initialTab: 'jobs' });
  }

  function markResolved() {
    healthStore.resolveAllFailures();
  }
</script>

<template>
  <div class="printing-panel">
    <div class="panel-header">
      <span class="panel-title">{{ $t('printWidget.panelTitle') }}</span>
      <v-btn
        icon
        small
        :title="$t('printWidget.openSettings')"
        @click="openSettings"
      >
        <v-icon small>mdi-cog</v-icon>
      </v-btn>
    </div>

    <div v-if="allGood" class="panel-all-good">
      <v-icon color="green" small>mdi-check-circle</v-icon>
      <span>{{ $t('printWidget.allGood') }}</span>
    </div>

    <template v-if="offline.length">
      <div class="panel-section-title">
        {{ $t('printWidget.offlineTitle') }}
      </div>
      <div
        v-for="entry in offline"
        :key="entry.key"
        class="panel-row offline-row"
      >
        <v-icon color="amber darken-2" small>mdi-printer-alert</v-icon>
        <div class="row-main">
          <div class="row-title">{{ entry.printer.name }}</div>
          <div class="row-hint">{{ $t('printWidget.printerOffline') }}</div>
        </div>
      </div>
    </template>

    <template v-if="failures.length">
      <div class="panel-section-title">
        {{ $t('printWidget.failuresTitle') }}
      </div>
      <div
        v-for="entry in failures"
        :key="entry.key"
        class="panel-row failure-row"
      >
        <v-icon
          :color="entry.kind === 'verify' ? 'amber darken-2' : 'red darken-1'"
          small
        >
          {{
            entry.kind === 'verify' ? 'mdi-help-circle' : 'mdi-alert-circle'
          }}
        </v-icon>
        <div class="row-main">
          <div class="row-title">
            {{ entry.title }}
            <span v-if="entry.count > 1" class="row-count"
              >×{{ entry.count }}</span
            >
          </div>
          <div v-if="entry.errorMessage" class="row-error">
            {{ entry.errorMessage }}
          </div>
          <div class="row-hint">
            {{
              entry.kind === 'verify'
                ? $t('printWidget.verifyHint')
                : $t('printWidget.retryableHint')
            }}
          </div>
        </div>
        <span class="row-time">{{ entryTime(entry) }}</span>
      </div>
    </template>

    <div class="panel-actions">
      <v-btn small depressed block color="primary" outlined @click="viewJobs">
        {{ $t('printWidget.viewJobs') }}
      </v-btn>
      <v-btn
        v-if="failures.length"
        small
        depressed
        block
        color="primary"
        @click="markResolved"
      >
        {{ $t('printWidget.markResolved') }}
      </v-btn>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .printing-panel {
    @apply tw-flex tw-w-[300px] tw-max-w-[90vw] tw-flex-col tw-gap-2 tw-bg-white tw-p-3;

    .panel-header {
      @apply tw-flex tw-items-center tw-justify-between;

      .panel-title {
        @apply tw-text-sm tw-font-bold tw-text-neutral-900;
      }
    }

    .panel-all-good {
      @apply tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-bg-green-50 tw-px-3 tw-py-2 tw-text-sm tw-text-green-800;
    }

    .panel-section-title {
      @apply tw-mt-1 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-neutral-500;
    }

    .panel-row {
      @apply tw-flex tw-items-start tw-gap-2 tw-rounded-lg tw-px-2 tw-py-1.5;

      &.offline-row {
        @apply tw-bg-amber-50;
      }

      &.failure-row {
        @apply tw-bg-red-50;
      }

      .row-main {
        @apply tw-min-w-0 tw-flex-1;

        .row-title {
          @apply tw-text-sm tw-font-medium tw-text-neutral-800;

          .row-count {
            @apply tw-ml-1 tw-text-xs tw-font-semibold tw-text-neutral-500;
          }
        }

        .row-error {
          @apply tw-truncate tw-text-xs tw-text-neutral-500;
        }

        .row-hint {
          @apply tw-text-xs tw-italic tw-text-neutral-600;
        }
      }

      .row-time {
        @apply tw-flex-none tw-text-xs tw-text-neutral-400;
      }
    }

    .panel-actions {
      @apply tw-mt-1 tw-flex tw-flex-col tw-gap-2;
    }
  }
</style>
